import { NavLink } from "react-router";
import {
  Gift,
  Tag,
  Wallet,
  TrendingUp,
  AlertCircle,
  ChevronRight,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

/**
 * Phase 16b (#269): Discounts admin hub.
 *
 * Lists every kind of discount rule that lowers a customer's price. Each
 * card maps 1:1 to a sub-route (or "Coming soon" if not yet built). The
 * hub is the stable nav anchor — new discount types add a card here rather
 * than churning the sidebar.
 *
 * Audit log of *applied* adjustments lives separately at /payments/adjustments
 * — different mental model (rules vs. events).
 */
export function DiscountsHubScreen() {
  return (
    <div className="p-4 space-y-4">
      <div>
        <h1 className="text-3xl font-bold">Discounts</h1>
        <p className="text-gray-600 mt-2 max-w-2xl">
          Rules that lower a customer's price. For applied-event history, see{" "}
          <NavLink to="/payments/adjustments" className="underline">
            Payments → Adjustments
          </NavLink>
          .
        </p>
      </div>

      <div className="space-y-3">
        <DiscountCard
          icon={<Gift className="h-5 w-5 text-emerald-700" />}
          title="Bundle pickup"
          description="Auto-applied when multiple shipments share one pickup booking."
          subline="Carrier-scoped curve for pickup provider carriers"
          status="active"
          actionHref="/pricing/discounts/bundle"
          actionLabel="Edit"
        />
        <DiscountCard
          icon={<Tag className="h-5 w-5 text-zinc-500" />}
          title="Promo codes"
          description="Customer-redeemed codes at checkout. Includes free-X codes (FREEPICKUP, FREEDELIVERY) via line-item scope."
          status="comingSoon"
        />
        <DiscountCard
          icon={<Wallet className="h-5 w-5 text-emerald-700" />}
          title="Customer vouchers"
          description="Pre-funded discounts on a customer's wallet — issued by admin (per customer) or tier-perk cron. Auto-redeemed at next eligible order."
          subline="Issue via Customers → ⋯ → Vouchers"
          status="active"
        />
        <DiscountCard
          icon={<TrendingUp className="h-5 w-5 text-zinc-500" />}
          title="Volume rebates"
          description="Auto-credit at month-end when customer monthly spend crosses a threshold."
          status="comingSoon"
        />
        <DiscountCard
          icon={<AlertCircle className="h-5 w-5 text-zinc-500" />}
          title="SLA-miss credits"
          description="Auto-refund a portion when a delivery commitment is missed."
          status="comingSoon"
        />
      </div>

      <div className="pt-4 border-t text-xs text-muted-foreground">
        See also:{" "}
        <NavLink to="/pricing/surcharges" className="underline">
          Additional Services
        </NavLink>{" "}
        ·{" "}
        <NavLink to="/payments/adjustments" className="underline">
          Applied adjustments log
        </NavLink>
      </div>
    </div>
  );
}

interface DiscountCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  subline?: string;
  status: "active" | "comingSoon";
  actionHref?: string;
  actionLabel?: string;
}

function DiscountCard({
  icon,
  title,
  description,
  subline,
  status,
  actionHref,
  actionLabel,
}: DiscountCardProps) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className="mt-0.5">{icon}</div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h2 className="font-semibold text-foreground">{title}</h2>
              {status === "active" ? (
                <Badge
                  variant="secondary"
                  className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100"
                >
                  Active
                </Badge>
              ) : (
                <Badge
                  variant="secondary"
                  className="bg-zinc-100 text-zinc-700 hover:bg-zinc-100"
                >
                  Coming soon
                </Badge>
              )}
            </div>
            <p className="text-sm text-muted-foreground mt-1">{description}</p>
            {subline && (
              <p className="text-xs text-muted-foreground mt-1 font-mono">
                {subline}
              </p>
            )}
          </div>
          <div className="shrink-0">
            {status === "active" && actionHref ? (
              <Button asChild variant="outline" size="sm">
                <NavLink to={actionHref}>
                  {actionLabel ?? "Edit"}
                  <ChevronRight className="h-3 w-3 ml-1" />
                </NavLink>
              </Button>
            ) : (
              <Button variant="outline" size="sm" disabled>
                —
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
